/// Day, month-word, year — in that order, always. A locale default puts the
/// month first for en-US readers, which is the bug this module exists to
/// prevent; only the month word itself localizes. Times are 24h, computed from
/// the parts rather than `hour12: false` (which yields "24:00" at midnight in
/// some locales).

type DateInput = string | number | Date;

interface Shape {
  /// "Aug" instead of "August" — for table cells and chips.
  short?: boolean;
  year?: boolean;
  time?: boolean;
}

function dayMonthYear(
  value: DateInput,
  { short = false, year = true, time = false }: Shape,
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";

  const month = d.toLocaleDateString(undefined, {
    month: short ? "short" : "long",
  });
  let out = `${d.getDate()} ${month}`;
  if (year) out += ` ${d.getFullYear()}`;
  if (time) {
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    out += `, ${hh}:${mm}`;
  }
  return out;
}

/// "28 August 2026"
export const formatDayMonthYear = (value: DateInput): string =>
  dayMonthYear(value, {});

/// "28 August 2026, 14:32"
export const formatDayMonthYearTime = (value: DateInput): string =>
  dayMonthYear(value, { time: true });

/// "28 Aug 2026"
export const formatShortDate = (value: DateInput): string =>
  dayMonthYear(value, { short: true });

/// "28 Aug 2026, 14:32"
export const formatShortDateTime = (value: DateInput): string =>
  dayMonthYear(value, { short: true, time: true });

/// "28 Aug, 14:32" — for lists where the year is implied by context.
export const formatDayMonthTime = (value: DateInput): string =>
  dayMonthYear(value, { short: true, year: false, time: true });

/// "14:32" — 24h, for a row whose day is established by its group heading.
export const formatTime = (value: DateInput): string => {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/// The heading a day's worth of events sits under: "Today", "Yesterday", or
/// the full date. Relative only for the two days a reader holds in their head.
export const formatDayLabel = (value: DateInput): string => {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  const midnight = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(new Date()) - midnight(d)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return formatDayMonthYear(d);
};

/// Groups events into days, newest first, keeping each day's own order.
export const groupByDay = <T>(
  items: T[],
  at: (item: T) => string,
): { key: string; label: string; items: T[] }[] => {
  const out: { key: string; label: string; items: T[] }[] = [];
  for (const item of items) {
    const stamp = at(item);
    const key = formatDayMonthYear(stamp);
    const last = out[out.length - 1];
    if (last?.key === key) last.items.push(item);
    else out.push({ key, label: formatDayLabel(stamp), items: [item] });
  }
  return out;
};
